import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate, Link, Outlet, useMatches } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Award, BookOpen, Check, FileQuestion, CheckCircle2, XCircle, ArrowLeft } from 'lucide-react';
import {
  certificationService,
  CertificationBlock,
} from '@/features/certification/services/certification.service';
import { usePermissions } from '@/common/hooks/usePermissions';
import { Permission } from '@/common/enums/permissions.enum';
import { useToast } from '@/hooks/use-toast';

export const Route = createFileRoute('/_authenticated/certification/courses/$courseId')({
  component: CertificationCoursePage,
});

function CertificationCoursePage() {
  const { courseId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const matches = useMatches();
  const { hasPermission } = usePermissions();
  const canView = hasPermission(Permission.CERTIFICATION_VIEW);
  const [selectedStageId, setSelectedStageId] = useState<string | null>(null);

  const { data: course, isLoading } = useQuery({
    queryKey: ['certification', 'course', courseId],
    queryFn: () => certificationService.getCourse(courseId),
    enabled: canView && !!courseId,
  });

  const { data: progress } = useQuery({
    queryKey: ['certification', 'progress', courseId],
    queryFn: () => certificationService.getProgress(courseId),
    enabled: canView && !!courseId,
  });

  const { data: certificate } = useQuery({
    queryKey: ['certification', 'certificate', courseId],
    queryFn: () => certificationService.getCertificateByCourse(courseId),
    enabled: canView && !!courseId && (progress?.progressPct ?? 0) >= 100,
  });

  const stages = useMemo(() => course?.stages ?? [], [course?.stages]);
  
  // Selecionar primeira etapa não concluída ao carregar o curso
  useEffect(() => {
    if (stages.length > 0 && !selectedStageId) {
      const completedStageIds = new Set(progress?.completedStageIds || []);
      
      // Encontrar primeira etapa não concluída
      const firstIncompleteStage = stages.find(
        (stage) => !completedStageIds.has(stage.id)
      );
      
      // Se todas estão concluídas, selecionar a última
      const stageToSelect = firstIncompleteStage || stages[stages.length - 1];
      
      if (stageToSelect) {
        setSelectedStageId(stageToSelect.id);
      }
    }
  }, [stages, progress, selectedStageId]);

  const completeStageMutation = useMutation({
    mutationFn: (stageId: string) =>
      certificationService.completeStage(courseId, stageId),
    onSuccess: (_, completedStageId) => {
      queryClient.invalidateQueries({ queryKey: ['certification', 'progress', courseId] });
      
      // Encontrar a etapa atual e ir para a próxima
      const currentIndex = stages.findIndex((s) => s.id === completedStageId);
      if (currentIndex !== -1 && currentIndex < stages.length - 1) {
        // Ir para a próxima etapa
        const nextStage = stages[currentIndex + 1];
        setSelectedStageId(nextStage.id);
        
        // Scroll para o topo do conteúdo
        setTimeout(() => {
          const mainElement = document.querySelector('main');
          if (mainElement) {
            mainElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    },
  });

  if (!canView) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Sem permissão para acessar este curso.</p>
      </div>
    );
  }

  if (isLoading || !course) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse text-muted-foreground">Carregando...</div>
      </div>
    );
  }

  const selectedStage = stages.find((s) => s.id === selectedStageId) ?? stages[0];
  const progressPct = progress?.progressPct ?? 0;
  const allStagesCompleted = progressPct >= 100;

  const hasChildRoute = matches.some(match => match.routeId.includes('/simulado'));

  if (hasChildRoute) {
    return <Outlet />;
  }

  return (
    <div className="flex flex-col flex-1 h-[calc(100vh-4rem)] overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <Link
          to="/certification"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar para cursos
        </Link>
      </div>
      <div className="flex flex-1 gap-6 px-6 pb-6 overflow-hidden">
        <aside className="w-64 shrink-0 space-y-2">
        <Card>
          <CardHeader className="pb-2">
            <h2 className="font-semibold text-sm">Módulos</h2>
          </CardHeader>
          <CardContent className="pt-0 space-y-1">
            {stages.map((stage) => {
              const completed = progress?.completedStageIds?.includes(stage.id);
              return (
                <button
                  key={stage.id}
                  type="button"
                  onClick={() => setSelectedStageId(stage.id)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center gap-2 transition-colors ${
                    selectedStage?.id === stage.id
                      ? 'bg-primary text-primary-foreground'
                      : completed
                      ? 'bg-green-50 hover:bg-green-100 dark:bg-green-950 dark:hover:bg-green-900'
                      : 'hover:bg-muted'
                  }`}
                >
                  {completed ? (
                    <Check className="h-4 w-4 shrink-0 text-green-600 dark:text-green-400" />
                  ) : (
                    <BookOpen className="h-4 w-4 shrink-0" />
                  )}
                  <span className="truncate flex-1">{stage.title}</span>
                  {completed && (
                    <span className="text-xs text-green-600 dark:text-green-400 font-medium shrink-0">
                      Concluída
                    </span>
                  )}
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => {
                console.log('[CoursePage] Link Simulado na sidebar clicado, courseId:', courseId);
                navigate({
                  to: '/certification/courses/$courseId/simulado',
                  params: { courseId },
                });
              }}
              className="flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-muted mt-2 w-full text-left"
            >
              <FileQuestion className="h-4 w-4" />
              Simulado
            </button>
          </CardContent>
        </Card>
        <div className="space-y-1">
          <Progress value={progressPct} className="h-2" />
          <p className="text-xs text-muted-foreground">{progressPct}% concluído</p>
        </div>
        {certificate && (
          <Button
            variant="outline"
            className="w-full"
            onClick={() =>
              navigate({
                to: '/certification/certificados/$certificateId',
                params: { certificateId: certificate.id },
              })
            }
          >
            <Award className="h-4 w-4 mr-2" />
            Ver certificado
          </Button>
        )}
        {allStagesCompleted && !certificate && (
          <Button 
            className="w-full"
            onClick={async () => {
              console.log('[CoursePage] Botão Fazer simulado clicado, courseId:', courseId);
              try {
                const result = await navigate({
                  to: '/certification/courses/$courseId/simulado',
                  params: { courseId },
                });
                console.log('[CoursePage] Navegação retornou:', result);
                console.log('[CoursePage] URL atual após navegação:', window.location.pathname);
              } catch (error) {
                console.error('[CoursePage] Erro na navegação:', error);
              }
            }}
          >
            Fazer simulado
          </Button>
        )}
      </aside>

      <main className="flex-1 min-w-0 overflow-hidden">
        {selectedStage && (
          <Card className="h-full flex flex-col overflow-hidden">
            <CardHeader>
              <h3 className="font-semibold">{selectedStage.title}</h3>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto min-w-0">
              {selectedStage.blocks?.length === 0 ? (
                <p className="text-muted-foreground text-sm">Nenhum conteúdo nesta etapa.</p>
              ) : (
                <div className="space-y-4">
                  {selectedStage.blocks
                    ?.sort((a, b) => a.order - b.order)
                    .map((block) => (
                      <BlockRenderer key={block.id} block={block} />
                    ))}
                </div>
              )}
              <div className="pt-4 pb-4">
                <Button
                  size="sm"
                  onClick={() =>
                    completeStageMutation.mutate(selectedStage.id)
                  }
                  disabled={
                    completeStageMutation.isPending ||
                    progress?.completedStageIds?.includes(selectedStage.id)
                  }
                >
                  {progress?.completedStageIds?.includes(selectedStage.id)
                    ? 'Concluído'
                    : 'Marcar como concluído'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
      </div>
    </div>
  );
}

// Função para converter URL do YouTube para formato de embed
function convertToEmbedUrl(url: string): string {
  if (!url) return '';
  
  // Se já é uma URL de embed, retorna como está
  if (url.includes('youtube.com/embed/')) {
    return url;
  }
  
  // Converter URL do YouTube (youtube.com/watch?v=VIDEO_ID)
  const youtubeWatchRegex = /(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/;
  const youtubeMatch = url.match(youtubeWatchRegex);
  
  if (youtubeMatch && youtubeMatch[1]) {
    const videoId = youtubeMatch[1];
    // Remover parâmetros adicionais se houver
    const cleanVideoId = videoId.split('&')[0];
    return `https://www.youtube.com/embed/${cleanVideoId}`;
  }
  
  // Se não for YouTube, retorna a URL original (pode ser outro serviço de vídeo)
  return url;
}

function BlockRenderer({ block }: { block: CertificationBlock }) {
  const content = block.content as Record<string, string> | undefined;
  
  const renderContent = () => {
    switch (block.type) {
      case 'text': {
        const textContent = content?.text ?? '';
        if (!textContent) return null;
        // Renderizar HTML formatado do editor
        return (
          <div 
            className="prose prose-sm max-w-none break-words dark:prose-invert rich-text-content"
            style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
            dangerouslySetInnerHTML={{ __html: textContent }}
          />
        );
      }
      case 'video': {
        const videoUrl = content?.url;
        if (!videoUrl) return null;
        const embedUrl = convertToEmbedUrl(videoUrl);
        return (
          <div className="aspect-video rounded-lg overflow-hidden bg-muted w-full">
            <iframe
              src={embedUrl}
              title="Vídeo"
              className="w-full h-full"
              allowFullScreen
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            />
          </div>
        );
      }
      case 'image': {
        const imageUrl = content?.url || content?.signedUrl || null;
        if (!imageUrl) return null;
        return (
          <figure className="w-full">
            <img
              src={imageUrl}
              alt={content?.alt ?? 'Imagem do conteúdo'}
              className="max-w-full h-auto rounded-lg w-full object-contain"
            />
          </figure>
        );
      }
      case 'audio':
        return (
          <audio controls className="w-full max-w-full" src={content?.url ?? content?.storageKey}>
            Áudio não suportado.
          </audio>
        );
      case 'drive_link':
        return (
          <a
            href={content?.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline break-all overflow-wrap-anywhere"
          >
            Abrir link no Drive
          </a>
        );
      case 'quiz':
        return <QuizBlock blockId={block.id} />;
      default:
        return null;
    }
  };

  const contentElement = renderContent();
  if (!contentElement) return null;

  return (
    <Card className="w-full min-w-0 break-words overflow-hidden">
      <CardContent className="p-4 break-words" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
        {contentElement}
      </CardContent>
    </Card>
  );
}

function QuizBlock({ blockId }: { blockId: string }) {
  const { toast } = useToast();
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, number>>({});
  const [submitted, setSubmitted] = useState(false);
  const [results, setResults] = useState<Record<string, boolean>>({});

  const { data: questions = [], isLoading } = useQuery({
    queryKey: ['certification', 'quiz-questions', blockId],
    queryFn: () => certificationService.getQuestionsByBlock(blockId),
    enabled: !!blockId,
  });

  const submitQuizMutation = useMutation({
    mutationFn: (answers: Array<{ questionId: string; answerIndex: number }>) =>
      certificationService.submitQuizAnswers(blockId, answers),
    onSuccess: (data) => {
      // Criar mapa de resultados
      const newResults: Record<string, boolean> = {};
      data.answers.forEach((answer) => {
        newResults[answer.questionId] = answer.isCorrect;
      });
      setResults(newResults);
      setSubmitted(true);
      
      toast({
        title: 'Quiz enviado!',
        description: `Você acertou ${data.correctCount} de ${data.totalQuestions} questões (${data.scorePct}%)`,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro',
        description: error?.response?.data?.message || 'Não foi possível enviar o quiz',
        variant: 'destructive',
      });
    },
  });

  // Tipar questões com explanation opcional
  type QuestionWithExplanation = {
    id: string;
    stem: string;
    options: string[];
    correctIndex: number;
    type: 'multiple_choice' | 'true_false';
    explanation?: string | null;
  };

  const handleAnswerSelect = (questionId: string, answerIndex: number) => {
    if (submitted) return;
    setSelectedAnswers((prev) => ({
      ...prev,
      [questionId]: answerIndex,
    }));
  };

  const handleSubmit = () => {
    if (questions.length === 0) {
      toast({
        title: 'Erro',
        description: 'Não há questões neste quiz',
        variant: 'destructive',
      });
      return;
    }

    // Verificar se todas as questões foram respondidas
    const allAnswered = questions.every((q) => selectedAnswers[q.id] !== undefined);
    if (!allAnswered) {
      toast({
        title: 'Atenção',
        description: 'Responda todas as questões antes de enviar',
        variant: 'destructive',
      });
      return;
    }

    // Preparar respostas para enviar
    const answers = questions.map((q) => ({
      questionId: q.id,
      answerIndex: selectedAnswers[q.id],
    }));

    // Enviar para o backend
    submitQuizMutation.mutate(answers);
  };

  if (isLoading) {
    return (
      <Card className="w-full">
        <CardContent className="p-4">
          <p className="text-sm text-muted-foreground">Carregando questões...</p>
        </CardContent>
      </Card>
    );
  }

  if (questions.length === 0) {
    return (
      <Card className="w-full">
        <CardContent className="p-4">
          <p className="text-sm text-muted-foreground">Este quiz ainda não possui questões.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="text-lg">Quiz</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {questions.map((question, questionIndex) => {
          const userAnswer = selectedAnswers[question.id];
          const showResult = submitted;

          return (
            <div key={question.id} className="space-y-3">
              <div className="flex items-start gap-2">
                <span className="font-medium text-sm text-muted-foreground min-w-[24px]">
                  {questionIndex + 1}.
                </span>
                <div className="flex-1">
                  <p className="font-medium mb-3">{question.stem}</p>
                  <div className="space-y-2">
                    {question.options.map((option, optionIndex) => {
                      const isSelected = userAnswer === optionIndex;
                      const isCorrectAnswer = optionIndex === question.correctIndex;
                      let optionStyle = '';

                      if (showResult) {
                        if (isCorrectAnswer) {
                          optionStyle = 'border-green-500 bg-green-50 dark:bg-green-950';
                        } else if (isSelected && !isCorrectAnswer) {
                          optionStyle = 'border-red-500 bg-red-50 dark:bg-red-950';
                        }
                      } else if (isSelected) {
                        optionStyle = 'border-primary bg-primary/10';
                      }

                      return (
                        <button
                          key={optionIndex}
                          type="button"
                          onClick={() => handleAnswerSelect(question.id, optionIndex)}
                          disabled={submitted}
                          className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${
                            optionStyle || 'border-border hover:border-primary/50'
                          } ${submitted ? 'cursor-default' : 'cursor-pointer'}`}
                        >
                          <div className="flex items-center gap-2">
                            {showResult && isCorrectAnswer && (
                              <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 shrink-0" />
                            )}
                            {showResult && isSelected && !isCorrectAnswer && (
                              <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 shrink-0" />
                            )}
                            {!showResult && isSelected && (
                              <div className="h-5 w-5 rounded-full border-2 border-primary bg-primary shrink-0" />
                            )}
                            {!showResult && !isSelected && (
                              <div className="h-5 w-5 rounded-full border-2 border-muted-foreground shrink-0" />
                            )}
                            <span className="flex-1">{option}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  {/* Mostrar explicação após submeter */}
                  {submitted && (question as QuestionWithExplanation).explanation && (
                    <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800">
                      <p className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">
                        Explicação:
                      </p>
                      <p className="text-sm text-blue-800 dark:text-blue-200">
                        {(question as QuestionWithExplanation).explanation}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {!submitted && (
          <div className="pt-4">
            <Button 
              onClick={handleSubmit} 
              className="w-full"
              disabled={submitQuizMutation.isPending}
            >
              {submitQuizMutation.isPending ? 'Enviando...' : 'Enviar respostas'}
            </Button>
          </div>
        )}

        {submitted && (
          <div className="pt-4 border-t">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Resultado:</span>
              <span className="text-sm font-medium">
                {Object.values(results).filter((r) => r).length} de {questions.length} corretas
              </span>
            </div>
            <Progress
              value={(Object.values(results).filter((r) => r).length / questions.length) * 100}
              className="h-2"
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
